import React, { Suspense, useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { isFeatureEnabled, loadFeatureTogglesFromSettings } from '../utils/featureFlags';
import { FEATURES } from '../features';

// Lazy load public components
const TenantLogin = React.lazy(() => import('./public/TenantLogin'));
const TenantMaintenanceDashboard = React.lazy(() => import('./public/TenantMaintenanceDashboard'));
const TenantMaintenanceReport = React.lazy(() => import('./public/TenantMaintenanceReport'));
const TenantMaintenanceView = React.lazy(() => import('./public/TenantMaintenanceView'));
const ContractorLogin = React.lazy(() => import('./public/ContractorLogin'));
const ContractorDashboard = React.lazy(() => import('./public/ContractorDashboard'));
const ContractorJobDetails = React.lazy(() => import('./public/ContractorJobDetails'));
const ContractorAgentMessages = React.lazy(() => import('./public/ContractorAgentMessages'));
const VendorLogin = React.lazy(() => import('./public/VendorLogin'));
const VendorPortal = React.lazy(() => import('./public/VendorPortal'));
// Public viewing-booking routes
const PublicBookingPage = React.lazy(() => import('./public/PublicBookingPage'));
const PublicBookingCalendarPage = React.lazy(() => import('./public/PublicBookingCalendarPage'));
const InspectionSignPage = React.lazy(() => import('./public/InspectionSignPage'));

// Loading component for public routes
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-background text-foreground">
    <div className="flex flex-col items-center">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <span className="mt-4 text-gray-600 dark:text-gray-400">Loading...</span>
    </div>
  </div>
);

/**
 * PublicApp - Public access routes for tenants and contractors
 * 
 * These routes don't require Firebase authentication.
 * Instead, they use email-based verification codes.
 */
const PublicApp = () => {
  const location = useLocation();
  const isCalendarWall = location.pathname.startsWith('/calendar/');
  const isTv = useMediaQuery('(min-width: 1280px)');
  const isCalendarTv = isCalendarWall && isTv;
  const [isTenantPortalEnabled, setIsTenantPortalEnabled] = useState(false);
  const [featureFlagsLoaded, setFeatureFlagsLoaded] = useState(false);

  // Load feature flags on mount
  useEffect(() => {
    const loadFeatureFlags = async () => {
      try {
        // Load feature toggles from Firestore
        await loadFeatureTogglesFromSettings();
        // Now check if tenant portal is enabled
        const tenantEnabled = isFeatureEnabled(FEATURES.TENANT_PORTAL);
        setIsTenantPortalEnabled(tenantEnabled);
        setFeatureFlagsLoaded(true);
      } catch (error) {
        console.error('Error loading feature flags:', error);
        // Fallback to default check
        const tenantEnabled = isFeatureEnabled(FEATURES.TENANT_PORTAL);
        setIsTenantPortalEnabled(tenantEnabled);
        setFeatureFlagsLoaded(true);
      }
    };

    loadFeatureFlags();
  }, []);

  // Show loading state while feature flags are being loaded
  if (!featureFlagsLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background text-foreground">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="mt-4 text-gray-600 dark:text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        isCalendarTv
          ? 'flex h-[100dvh] max-h-[100dvh] w-full flex-col overflow-hidden bg-background text-foreground'
          : 'min-h-screen w-full bg-background text-foreground'
      }
      style={
        isCalendarTv
          ? {
              paddingLeft: 'env(safe-area-inset-left, 0px)',
              paddingRight: 'env(safe-area-inset-right, 0px)',
            }
          : undefined
      }
    >
      <Suspense fallback={<LoadingFallback />}>
        <div className={isCalendarTv ? 'flex min-h-0 flex-1 flex-col' : undefined}>
        <Routes>
          {/* Tenant Routes - Protected by feature flag */}
          {isTenantPortalEnabled ? (
            <>
              <Route path="/tenant" element={<TenantLogin />} />
              <Route path="/tenant/maintenance" element={<TenantMaintenanceDashboard />} />
              <Route path="/tenant/report" element={<TenantMaintenanceReport />} />
              <Route path="/tenant/view/:requestId" element={<TenantMaintenanceView />} />
            </>
          ) : (
            <>
              {/* Redirect all tenant routes to login if feature is disabled */}
              <Route path="/tenant" element={<Navigate to="/login" replace />} />
              <Route path="/tenant/maintenance" element={<Navigate to="/login" replace />} />
              <Route path="/tenant/report" element={<Navigate to="/login" replace />} />
              <Route path="/tenant/view/:requestId" element={<Navigate to="/login" replace />} />
            </>
          )}

          {/* Contractor Routes */}
          <Route path="/contractor" element={<ContractorLogin />} />
          <Route path="/contractor/dashboard" element={<ContractorDashboard />} />
          <Route path="/contractor/job/:jobId" element={<ContractorJobDetails />} />
          <Route path="/contractor/job/:jobId/messages" element={<ContractorAgentMessages />} />

          {/* Vendor Routes - read-only sales progress portal.
              /vendor ALWAYS renders the vendor login form (never the internal app login),
              even when the VENDOR_PORTAL feature flag is off. The vendor login form itself
              handles the "no matching vendor account" case via the failed-login UI, which is
              a far better experience than bouncing sellers to the internal agency login page. */}
          <Route path="/vendor" element={<VendorLogin />} />
          <Route path="/vendor/property" element={<VendorPortal />} />

          {/* Public viewing booking routes - token-gated, no auth required */}
          <Route path="/book/:bookingToken" element={<PublicBookingPage />} />
          <Route path="/calendar/:secretToken" element={<PublicBookingCalendarPage />} />

          {/* Inspection report e-sign — opaque signingToken, anonymous Storage auth */}
          <Route path="/sign/:token" element={<InspectionSignPage />} />

          {/* Default redirect */}
          <Route path="/tenant/*" element={isTenantPortalEnabled ? <Navigate to="/tenant" replace /> : <Navigate to="/login" replace />} />
          <Route path="/contractor/*" element={<Navigate to="/contractor" replace />} />
          <Route path="/vendor/*" element={<Navigate to="/vendor" replace />} />
        </Routes>
        </div>
      </Suspense>
    </div>
  );
};

export default PublicApp;

